import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useToast } from '../../contexts/ToastContext';

const useAllocationKeys = () => {
  const { showToast } = useToast();
  const [forceRefresh, setForceRefresh] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });
  
  const queryClient = useQueryClient();

  const fetchAllocationKeys = async () => {
    try {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getAllocationKeys`
      );
      
      if (!response) throw new Error('Failed to fetch allocation keys');
      
      const keysArray = Array.isArray(response) ? response : 
                        (Array.isArray(response.data) ? response.data : []); 
      
      if (keysArray.length > 0) {
        setLastUpdated(new Date());
      }
      
      return keysArray;
    } catch (error) {
      console.error('Error fetching allocation keys:', error);
      throw error;
    }
  };

  const {
    data: allocationKeys = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['allocationKeys', forceRefresh],
    queryFn: fetchAllocationKeys,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    staleTime: 0
  });

  const refreshPriorityKeys = async () => {
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/refreshAllocationKeys`
      );
      
      if (response && response.success) {
        const keysCount = response.count || 0;
        
        showToast(`מפתחות ההעמסה עודכנו בהצלחה (${keysCount} מפתחות)`, "success");
        setLastUpdated(new Date());
        refreshData();
        return true;
      } else {
        throw new Error(response?.message || "Refresh failed");
      }
    } catch (error) {
      console.error("Error refreshing keys:", error);
      showToast("שגיאה ברענון מפתחות העמסה", "error");
      return false;
    }
  };

  const refreshData = () => {
    queryClient.invalidateQueries(['allocationKeys']);
    setForceRefresh(prev => prev + 1);
  };

  const formatDate = (date) => {
    if (!date) {
      return "לא ידוע";
    }
    
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return "תאריך לא תקין";
      }
      
      return dateObj.toLocaleString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return "שגיאה בתאריך";
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  return {
    allocationKeys,
    isLoading,
    error,
    lastUpdated,
    refreshPriorityKeys,
    refreshData,
    formatDate
  };
};

export default useAllocationKeys;